import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface StudioNavItem {
  id: string;
  title: string;
  subtitle?: string;
  badges?: Array<{ label: string; variant?: 'default' | 'secondary' | 'outline' | 'destructive' }>;
}

interface StudioNavListProps {
  items: StudioNavItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Enable drag-and-drop reordering. Callback receives the new ordered array. */
  onReorder?: (reorderedItems: StudioNavItem[]) => void;
}

function SortableNavItem({
  item,
  isSelected,
  onSelect,
  sortable,
}: {
  item: StudioNavItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  sortable: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !sortable });

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.7 : 1,
      }
    : undefined;

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className={cn(
        'flex items-center gap-1 rounded-lg transition-colors border',
        isSelected
          ? 'bg-primary/10 border-primary/40 font-medium'
          : 'border-transparent hover:bg-muted/50'
      )}
    >
      {sortable && (
        <button
          type="button"
          className="flex-shrink-0 p-1.5 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={() => onSelect(item.id)}
        className={cn(
          'flex-1 text-left px-2 py-2.5 text-sm min-w-0',
          !sortable && 'px-3'
        )}
      >
        <span className="block truncate text-foreground">{item.title}</span>
        {item.subtitle && (
          <span className="block text-xs text-muted-foreground truncate mt-0.5">
            {item.subtitle}
          </span>
        )}
        {item.badges && item.badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.badges.map((b, i) => (
              <Badge key={i} variant={b.variant ?? 'secondary'} className="text-[10px] px-1.5 py-0">
                {b.label}
              </Badge>
            ))}
          </div>
        )}
      </button>
    </div>
  );
}

export function StudioNavList({ items, selectedId, onSelect, onReorder }: StudioNavListProps) {
  const sortable = !!onReorder;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  if (sortable) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {items.map((item) => (
              <SortableNavItem
                key={item.id}
                item={item}
                isSelected={item.id === selectedId}
                onSelect={onSelect}
                sortable
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <div className="space-y-1">
      {items.map((item) => (
        <SortableNavItem
          key={item.id}
          item={item}
          isSelected={item.id === selectedId}
          onSelect={onSelect}
          sortable={false}
        />
      ))}
    </div>
  );
}
